/**
 * Utility functions for timezone handling
 * Gets timezone from app setup and formats dates accordingly
 */

/**
 * Maps timezone abbreviations to IANA timezone names
 * @param {string} tzAbbr - Timezone abbreviation (EST, PST, UTC, etc.)
 * @returns {string} IANA timezone name
 */
export const getTimezoneFromAbbr = (tzAbbr) => {
  if (!tzAbbr) return "America/New_York"; // Default to EST

  const timezoneMap = {
    EST: "America/New_York",
    EDT: "America/New_York",
    PST: "America/Los_Angeles",
    PDT: "America/Los_Angeles",
    CST: "America/Chicago",
    CDT: "America/Chicago",
    MST: "America/Denver",
    MDT: "America/Denver",
    UST: "America/New_York", // Assuming UST is same as EST
    UTC: "UTC",
  };

  return timezoneMap[tzAbbr.toUpperCase()] || "America/New_York";
};

/**
 * Gets the latest app setup from Redux store
 * @param {Object} appSetupState - Redux app setup state
 * @returns {Object|null} Latest app setup item or null
 */
export const getLatestAppSetup = (appSetupState) => {
  if (!appSetupState) return null;

  const items = appSetupState.items || [];
  if (items.length === 0) return null;

  // Sort by updated_at first, then created_at if updated_at is not available
  const sortedItems = [...items].sort((a, b) => {
    const dateA = new Date(a?.updated_at || a?.created_at || 0).getTime();
    const dateB = new Date(b?.updated_at || b?.created_at || 0).getTime();
    return dateB - dateA; // Descending order (latest first)
  });

  return sortedItems[0];
};

/**
 * Gets the timezone from app setup state
 * @param {Object} appSetupState - Redux app setup state
 * @returns {string} IANA timezone name (defaults to "America/New_York")
 */
export const getAppTimezone = (appSetupState) => {
  const latestAppSetup = getLatestAppSetup(appSetupState);
  if (!latestAppSetup) return "America/New_York";

  const tzAbbr = latestAppSetup.application_time_zone;
  return getTimezoneFromAbbr(tzAbbr);
};

/**
 * Gets the timezone abbreviation from app setup state
 * @param {Object} appSetupState - Redux app setup state
 * @returns {string} Timezone abbreviation (defaults to "EST")
 */
export const getAppTimezoneAbbr = (appSetupState) => {
  const latestAppSetup = getLatestAppSetup(appSetupState);
  if (!latestAppSetup) return "EST";

  return latestAppSetup.application_time_zone || "EST";
};

/**
 * Formats a date string to the app's configured timezone
 * Format: "YYYY-MM-DD, h:mm AM/PM TIMEZONE"
 * @param {string} dateString - ISO 8601 date string
 * @param {Object} appSetupState - Redux app setup state (optional, will use store if not provided)
 * @returns {string} Formatted date string
 */
export const formatDateToAppTimezone = (dateString, appSetupState = null) => {
  if (!dateString) return "";

  // Get app setup state from Redux store if not provided
  let appSetup = appSetupState;
  if (!appSetup && typeof window !== "undefined" && window.__REDUX_STORE__) {
    const state = window.__REDUX_STORE__.getState();
    appSetup = state.appSetup;
  }

  const timezone = getAppTimezone(appSetup);
  const tzAbbr = getAppTimezoneAbbr(appSetup);

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  // Convert to app timezone
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === "year")?.value || "";
  const month = parts.find((p) => p.type === "month")?.value || "";
  const day = parts.find((p) => p.type === "day")?.value || "";
  const hour = parts.find((p) => p.type === "hour")?.value || "";
  const minute = parts.find((p) => p.type === "minute")?.value || "";
  const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value || "";

  const formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const formattedTime = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")} ${dayPeriod.toUpperCase()}`;

  return `${formattedDate}, ${formattedTime} ${tzAbbr}`;
};

/**
 * Formats a date string to the app's configured timezone (alternative format)
 * Format: "YYYY-MM-DD at h:mm AM/PM TIMEZONE"
 * @param {string} dateString - ISO 8601 date string
 * @param {Object} appSetupState - Redux app setup state (optional)
 * @returns {string} Formatted date string
 */
export const formatDateToAppTimezoneAlt = (dateString, appSetupState = null) => {
  if (!dateString) return "";

  // Get app setup state from Redux store if not provided
  let appSetup = appSetupState;
  if (!appSetup && typeof window !== "undefined" && window.__REDUX_STORE__) {
    const state = window.__REDUX_STORE__.getState();
    appSetup = state.appSetup;
  }

  const timezone = getAppTimezone(appSetup);
  const tzAbbr = getAppTimezoneAbbr(appSetup);

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  // Convert to app timezone
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const parts = formatter.formatToParts(date);
  const year = parts.find((p) => p.type === "year")?.value || "";
  const month = parts.find((p) => p.type === "month")?.value || "";
  const day = parts.find((p) => p.type === "day")?.value || "";
  const hour = parts.find((p) => p.type === "hour")?.value || "";
  const minute = parts.find((p) => p.type === "minute")?.value || "";
  const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value || "";

  const formattedDate = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const formattedTime = `${hour.padStart(2, "0")}:${minute.padStart(2, "0")} ${dayPeriod.toUpperCase()}`;

  return `${formattedDate} at ${formattedTime} ${tzAbbr}`;
};

/**
 * Converts UTC string to formatted time in app's configured timezone
 * Format: "YYYY-MM-DD at h:mm AM/PM TIMEZONE"
 * @param {string} utcString - UTC date string
 * @param {Object} appSetupState - Redux app setup state (optional)
 * @returns {string} Formatted date string
 */
export const convertUtcToAppTimezone = (utcString, appSetupState = null) => {
  if (!utcString) return "";

  // Get app setup state from Redux store if not provided
  let appSetup = appSetupState;
  if (!appSetup && typeof window !== "undefined" && window.__REDUX_STORE__) {
    const state = window.__REDUX_STORE__.getState();
    appSetup = state.appSetup;
  }

  const timezone = getAppTimezone(appSetup);
  const tzAbbr = getAppTimezoneAbbr(appSetup);

  // Ensure valid ISO string (add 'Z' to indicate UTC if missing)
  const cleanUtc = utcString.split(".")[0];
  const isoUtc = cleanUtc.endsWith("Z") ? cleanUtc : `${cleanUtc}Z`;
  const date = new Date(isoUtc);

  if (isNaN(date.getTime())) return "";

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const parts = formatter.formatToParts(date);

  const year = parts.find((p) => p.type === "year")?.value || "";
  const month = parts.find((p) => p.type === "month")?.value || "";
  const day = parts.find((p) => p.type === "day")?.value || "";
  const hour = parts.find((p) => p.type === "hour")?.value || "";
  const minute = parts.find((p) => p.type === "minute")?.value || "";
  const dayPeriod = parts.find((p) => p.type === "dayPeriod")?.value || "";

  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")} at ${hour.padStart(2, "0")}:${minute.padStart(2, "0")} ${dayPeriod.toUpperCase()} ${tzAbbr}`;
};

/**
 * Gets the IANA timezone name for use in toLocaleString and similar functions
 * @param {Object} appSetupState - Redux app setup state (optional)
 * @returns {string} IANA timezone name
 */
export const getAppTimezoneName = (appSetupState = null) => {
  // Get app setup state from Redux store if not provided
  let appSetup = appSetupState;
  if (!appSetup && typeof window !== "undefined" && window.__REDUX_STORE__) {
    const state = window.__REDUX_STORE__.getState();
    appSetup = state.appSetup;
  }

  return getAppTimezone(appSetup);
};



